import { useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { CalendarService } from '@/types'

type ServicePickerProps = {
  services: CalendarService[]
  value: CalendarService | null
  onChange: (service: CalendarService | null) => void
  disabled?: boolean
  error?: string
}

export function ServicePicker({ services, value, onChange, disabled, error }: ServicePickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const results = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return services
    return services.filter((service) => service.name.toLowerCase().includes(q))
  }, [services, query])

  if (value) {
    return (
      <div className="space-y-2">
        <Label>Servicio</Label>
        <div className="flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 text-sm">
          <span className="truncate">
            {value.emoji ? `${value.emoji} ` : ''}
            {value.name} ({value.duration_minutes} min)
          </span>
          {!disabled && (
            <button
              type="button"
              className="ml-2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                onChange(null)
                setQuery('')
                setOpen(true)
              }}
            >
              Cambiar
            </button>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    )
  }

  return (
    <div
      className="space-y-2"
      ref={wrapRef}
      onBlur={(event) => {
        if (!wrapRef.current?.contains(event.relatedTarget as Node)) setOpen(false)
      }}
    >
      <Label htmlFor="service-search">Servicio</Label>
      <Input
        id="service-search"
        value={query}
        placeholder="Buscar servicio..."
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
      />
      {open && !disabled && (
        <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-sm">
          {results.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No se encontraron servicios</p>
          )}
          {results.map((service) => (
            <button
              key={service.id}
              type="button"
              className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-accent"
              onClick={() => {
                onChange(service)
                setOpen(false)
              }}
            >
              <span className="font-medium">
                {service.emoji ? `${service.emoji} ` : ''}
                {service.name}
              </span>
              <span className="text-xs text-muted-foreground">{service.duration_minutes} min</span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
